import type { SessionEvent } from '@aera/schema';
import { useEffect, useRef, useState } from 'react';

/**
 * Subscribes to GET /api/s/:token/events. EventSource reconnects on its own after a network blip; if the
 * stream is closed for good (proxy timeout, 5xx) it is reopened with backoff, and `onPoll` runs while the
 * stream is down so the screen still moves forward without it. The server keeps no replay, so `onReopen`
 * runs when the stream comes back after a drop: whatever was published meanwhile is gone.
 */
export function useSessionEvents(
  url: string | null,
  onEvent: (event: SessionEvent) => void,
  onPoll?: () => void,
  onReopen?: () => void,
): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const handler = useRef(onEvent);
  const poller = useRef(onPoll);
  const reopened = useRef(onReopen);
  handler.current = onEvent;
  poller.current = onPoll;
  reopened.current = onReopen;

  useEffect(() => {
    if (!url || typeof EventSource === 'undefined') {
      if (url && onPoll) {
        const id = setInterval(() => poller.current?.(), 4000);
        return () => clearInterval(id);
      }
      return undefined;
    }
    let source: EventSource | null = null;
    let retry = 0;
    let reopen: ReturnType<typeof setTimeout> | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let disposed = false;
    let dropped = false;

    const stopPolling = () => {
      if (poll) clearInterval(poll);
      poll = null;
    };
    const startPolling = () => {
      if (!poll && poller.current) poll = setInterval(() => poller.current?.(), 4000);
    };

    const open = () => {
      if (disposed) return;
      source = new EventSource(url, { withCredentials: true });
      source.onopen = () => {
        retry = 0;
        setConnected(true);
        stopPolling();
        if (dropped) reopened.current?.();
        dropped = false;
      };
      source.onmessage = (msg) => {
        try {
          handler.current(JSON.parse(msg.data as string) as SessionEvent);
        } catch {
          /* ignore malformed frames */
        }
      };
      source.onerror = () => {
        dropped = true;
        setConnected(false);
        startPolling();
        if (source?.readyState === EventSource.CLOSED) {
          source.close();
          const delay = Math.min(15000, 1000 * 2 ** retry++);
          reopen = setTimeout(open, delay);
        }
      };
    };
    open();
    return () => {
      disposed = true;
      source?.close();
      if (reopen) clearTimeout(reopen);
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return { connected };
}
